<template>
  <RoleDetail
      :title="pageTitle"
      :return-destination="'/SiteRole'"
      :show-edit-button="$isFunctionShown(define.PermissionKeys.Account.SiteRole.Detail.EDIT)"
      :disable-edit-button="$isFunctionDisabled(define.PermissionKeys.Account.SiteRole.Detail.EDIT)"
      :show-delete-button="$isFunctionShown(define.PermissionKeys.Account.SiteRole.Detail.DELETE)"
      :disable-delete-button="$isFunctionDisabled(define.PermissionKeys.Account.SiteRole.Detail.DELETE)"
      :show-back-button="showBackButton"
      :show-action-button="showActionButton"
      :show-save-button="showSaveButton"
      :disable-save-button="disableSaveButton"
      :show-cancel-button="showCancelButton"
      :bdmd-id="originData.name"
      :store-path="storePath"
      :path-parameter="['uuid']"
      :sorted-list="sortedList"
      :can-move-data="canMoveData"
      bdmd-title="ロール名称"
      router-path="/SiteRole/"
      @edit="edit"
      @delete="remove"
      @save="save"
      @cancel="cancel">
    <BaseTableDetail
        slot="list"
        class="siteroledetail-table-list"
        :border="true"
        :table-set-list="tableSetList"
        :display-item="detailData"
        :editable="editable" />
  </RoleDetail>
</template>

<script>
import _ from 'lodash'
import { mapState, mapMutations, mapGetters } from 'vuex'
import store from '@/store/index'

import * as define from '@/define'
import * as query from '@/queries/siteRole'
import common from '@/utils/common'
import * as config from '@/config'
import message from '@/utils/message'
import siteRole from '@/store/pages/siteRole'

import x from '@/components/mixins/mixin'
import confirmDiscardOnLeave from '@/components/mixins/confirmDiscardOnLeave'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import RoleDetail from '@/components/pages/account/RoleDetail'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import detailUtil from '@/components/mixins/detailUtil'

const EDITABLE = Object.freeze({
  [define.ROLE_MODE.NORMAL]: Object.freeze({}),
  [define.ROLE_MODE.EDIT]: Object.freeze({ name: true, site: true }),
  [define.ROLE_MODE.NEW]: Object.freeze({ name: true, site: true })
})

const returnDestination = '/SiteRole'
const returnDestinationTitle = 'CCS - サイトロール一覧'

export default {
  name: 'SiteRoleDetail',
  components: {
    RoleDetail,
    BaseTableDetail
  },
  mixins: [ x, confirmDiscardOnLeave, detailUtil ],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      detailData: {},
      originData: {},
      isChanged: false,
      reservedTransition: false,
      canMoveData: false
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      size: (state) => state.SiteRole.size,
      mode: (state) => state.SiteRole.mode
    }),
    ...mapGetters('LocalStorage', {
      sortCondition: 'sortCondition'
    }),
    storePath () {
      return 'SiteRole'
    },
    pageTitle () {
      return this.mode === define.ROLE_MODE.NEW
        ? 'サイトロール登録'
        : 'サイトロール詳細'
    },
    tableSetList () {
      return [
        {
          label: 'ロール名称',
          properties: [
            {
              path: 'name', type: 'String', elementType: ELEMENT_TYPE.INPUT, editableId: 'name',
              validateRule: 'required|max:100', validateAs: 'ロール名称'
            }
          ]
        }, {
          label: 'サイト',
          properties: [
            {
              path: 'sites', type: 'String', codeName: 'sites', selectCode: 'sites', editableId: 'site',
              elementType: ELEMENT_TYPE.CHECK_BOX, horizontal: false
            }
          ]
        }
      ]
    },
    sortedList () {
      return this.$store.getters['sortedList'](this.storePath, this.sortCondition('siteRoleList'))
    },
    showBackButton () {
      return this.mode === define.ROLE_MODE.NORMAL && !this.$isFunctionDisabled(define.PermissionKeys.Account.SiteRole.LIST)
    },
    showActionButton () {
      return this.mode === define.ROLE_MODE.NORMAL
    },
    showSaveButton () {
      return this.mode !== define.ROLE_MODE.NORMAL
    },
    disableSaveButton () {
      return !this.isChanged
    },
    showCancelButton () {
      return this.mode !== define.ROLE_MODE.NORMAL
    },
    editable () {
      return EDITABLE[this.mode]
    }
  },
  watch: {
    detailData: {
      handler: function (val, oldVal) {
        this.isChanged = this.$_ConfirmDiscardOnLeave_isChanged()
      },
      deep: true
    },
    mode (val) {
      // 既に遷移予定である場合、制御不要
      if (!this.reservedTransition) {
        const route = this.$route
        if (val === define.ROLE_MODE.NORMAL &&
          (route.params && route.params.id === '0')) {
          this.$router.push({ path: returnDestination })
        }
        // ページタイトルの再設定
        document.title = `CCS - ${this.pageTitle}`
      }
    }
  },
  async created () {
    // ページタイトルの設定
    document.title = `CCS - ${this.pageTitle}`
    window.addEventListener('beforeunload', this.onBeforeUnload)
    try {
      await this.getDetailData()
      if (this.mode === define.ROLE_MODE.NEW) {
        this.originData = _.cloneDeep(this.detailData)
        this.$_ConfirmDiscardOnLeave_watchStart(this.detailData, 'サイトロール登録を中止しますか？')
      }
    } catch (error) {
      this.$notify.error({ error: error })
      throw error
    }
  },
  destroyed () {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeRouteEnter (to, from, next) {
    // [beforeRouteEnter]ではvueコンポーネントの生成が未完であるため、「this.$store」で呼び出しができない。
    // そのため、storeのあるファイルをImportし、直接参照している。
    if (to.params && to.params.id === '0' && store.state.SiteRole.mode !== define.ROLE_MODE.NEW) {
      // 履歴の操作により、入ってきたとき、storeが正しくないので編集
      store.dispatch({ type: 'set', states: { 'SiteRole/mode': define.ROLE_MODE.NEW } })
    } else if (to.params && to.params.id !== '0' && store.state.SiteRole.mode !== define.ROLE_MODE.NORMAL) {
      next(vm => {
        vm.updateMode(define.ROLE_MODE.NORMAL)
      })
      return
    }

    next()
  },
  async beforeRouteUpdate (to, from, next) {
    // 新規作成完了後
    if (from.params && from.params.id === '0') {
      // 戻る等でいきなり新規作成とならないように履歴を修正
      document.title = returnDestinationTitle
      history.replaceState(null, null, returnDestination)
    }
    // ページ送りにより変更した対象アカウントのデータ取得（ページ送り時のモードは通常モードに制御されているため、初期化不要）
    next()
    await this.getDetailData()
  },
  beforeRouteLeave (to, from, next) {
    // 別ページへ移動する場合、modeを初期化する.
    this.reservedTransition = true
    this.updateMode(define.ROLE_MODE.NORMAL)

    next()
  },
  methods: {
    ...mapMutations('SiteRole', {
      updateListItem: 'update',
      deleteListItem: 'delete'
    }),
    edit () {
      this.updateMode(define.ROLE_MODE.EDIT)
      this.originData = _.cloneDeep(this.detailData)
      this.$_ConfirmDiscardOnLeave_watchStart(this.detailData)
    },
    remove () {
      this.$confirm('サイトロールを削除しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        try {
          this.$loadingService.show()
          await this.deleteRoleApi()
          this.deleteListItem(this.detailData.uuid)
          this.$notify.success('サイトロールを削除しました。')
          this.updateMode(define.ROLE_MODE.NORMAL)
          this.$router.push({ path: returnDestination })
          // 最新サイトロールの取得（アカウントの検索条件用）
          await siteRole.getSiteRoleOptions(true)
        } catch (error) {
          const data = _.get(error, 'response.data')
          if (data == null) {
            common.addError(error, { msg: message.MSG_502() })
          } else {
            common.addError(error, { msg: message.MSG_516() })
          }
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
      }).catch(() => {})
    },
    async save () {
      if (!await this.$validator.validateAll()) {
        // バリデーションエラーあり
        return
      }

      this.$confirm('保存しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        try {
          this.$loadingService.show()
          const res = await this.updateOrInsertSiteRoleApi()
          this.$_ConfirmDiscardOnLeave_watchEnd()
          this.isChanged = false
          if (this.mode === define.ROLE_MODE.NEW) {
            this.reservedTransition = true
            this.updateMode(define.ROLE_MODE.NORMAL)
            this.$router.push({ path: `/siterole/${res.data.uuid}` })
          } else {
            this.updateMode(define.ROLE_MODE.NORMAL)
            await this.getDetailData(true)
          }
          this.$notify.success('保存しました。')
          // 最新サイトロールの取得（アカウントの検索条件用）
          await siteRole.getSiteRoleOptions(true)
        } catch (error) {
          const data = _.get(error, 'response.data')
          if (data == null) {
            common.addError(error, { msg: message.MSG_502() })
          } else if (this.hasLastUpdatedError(error)) {
            common.addError(error, { msg: message.MSG_529() })
          } else {
            if (this.mode === define.ROLE_MODE.NEW) {
              common.addError(error, { msg: message.MSG_513() })
            } else {
              common.addError(error, { msg: message.MSG_514() })
            }
          }
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
      }).catch(() => {
        this.$notify.info('キャンセルしました。')
      })
    },
    cancel () {
      const cancelEdit = () => {
        this.updateMode(define.ROLE_MODE.NORMAL)
        this.$_ConfirmDiscardOnLeave_watchEnd()
        this.isChanged = false
      }
      if (!this.isChanged) {
        cancelEdit()
        return
      }
      this.$confirm('変更を破棄しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.detailData = _.cloneDeep(this.originData)
        cancelEdit()
      }).catch(e => e)
    },
    /**
     * サイトロール詳細情報を取得します。
     * @param {Boolean} [updateList=false] 一覧の情報を更新する場合、true
     */
    async getDetailData (updateList = false) {
      try {
        this.$loadingService.show()
        if (this.mode !== define.ROLE_MODE.NEW) {
          let res = await common.postGraphQl(query.GRAPH_QL_QUERY_SITE_ROLE_DETAIL)

          // 結果が null の場合
          if (res.data.data.siteRoles == null) {
            this.$notify.error({
              message: 'データを取得できませんでした。'
            })
            return
          }
          this.detailData = _.find(res.data.data.siteRoles, (element) => element.uuid === this.$route.params.id)
          if (!this.$route.params.id) {
            // 高速でページが切り替わったとき「this.$route.params.id」が存在しないため、本チェックを行う
            return
          }
          if (!this.detailData) {
            this.$notify.error({
              message: 'データを取得できませんでした。'
            })
            return
          }
          // listIndexの更新
          this.$_detailUtil_onGetDetailData((element) => element.uuid === this.$route.params.id)

          this.detailData.sites = _.map(this.detailData.sites, 'code')
        } else {
          this.detailData = {
            name: '',
            uuid: '',
            sites: []
          }
        }

        this.originData = _.cloneDeep(this.detailData)

        // 一覧更新
        if (updateList) {
          this.updateListItem(this.detailData)
        }
      } catch (error) {
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    /**
     * サイトロール登録 API またはサイトロール更新 APIを呼び出す
     */
    async updateOrInsertSiteRoleApi () {
      const url = this.mode === define.ROLE_MODE.NEW
        ? config.ROLE_SITE_URL
        : config.ROLE_SITE_URL + '/' + this.detailData.uuid
      const method = this.mode === define.ROLE_MODE.NEW ? 'post' : 'put'
      const response = await common.apiAxios(url,
        {
          method,
          data: { name: this.detailData.name, sites: _.map(this.detailData.sites, (val) => { return { code: val } }) },
          heads: { 'X-CCS-Last-Updated-At': this.detailData.updatedAt }
        })
      return response
    },
    async deleteRoleApi () {
      const url = config.ROLE_SITE_URL + '/' + this.detailData.uuid
      const method = 'delete'
      const response = await common.apiAxios(url, { method })
      return response
    },
    hasLastUpdatedError (error) {
      const code = _.get(error, 'response.data.code')
      if (code !== 400) {
        return false
      }
      return _.chain(error)
        .get('response.data.errors')
        .find(val => val.location === 'lastUpdatedAt' && val.reason === 'invalid')
        .value() != null
    },
    updateMode (mode) {
      this.$store.commit({
        type: 'set',
        states: {
          [`${this.storePath}/mode`]: mode
        }
      })
    },
    onBeforeUnload (event) {
      if (this.isChanged) {
        // 変更有の場合、再読み込み確認ダイアログを表示する.
        event.returnValue = 'changed'
      }
    }
  }
}
</script>
